import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Phone, Check, ChevronRight, ArrowLeft, MapPin, Clock, UserRound } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import Gallery from "./Gallery";
import { CLINIC, SPECIALTIES, DOCTORS } from "@/data/content";

const GAL_CAT = { "stomatolog-timisoara": "stomatologie", "laborator-analize": "laborator", "ginecolog-timisoara": "ginecologie", "medic-familie-timisoara": "familie" };
const DOC_SPECS = {
  "ginecolog-timisoara": ["Obstetrică-Ginecologie", "Ginecologie"],
  "medic-familie-timisoara": ["Medicină de Familie"],
  "stomatolog-timisoara": ["Stomatologie"],
  "laborator-analize": ["Medicină de Laborator"],
};

export default function SpecialtyPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const spec = SPECIALTIES.find((s) => s.slug === slug);

  useEffect(() => {
    if (!spec) return;
    document.title = spec.seoTitle;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", spec.metaDescription);
    return () => { document.title = "MEDAS Timișoara | Laborator Analize Medicale & Clinică"; };
  }, [spec]);

  if (!spec) {
    return (
      <div className="pt-40 pb-32 text-center">
        <h1 className="font-display text-3xl font-bold text-[#2A2521]">Specialitatea nu a fost găsită</h1>
        <button onClick={() => navigate("/")} className="mt-6 inline-flex items-center gap-2 text-[#A6823B] font-semibold">
          <ArrowLeft className="w-4 h-4" /> Înapoi la pagina principală
        </button>
      </div>
    );
  }

  const Icon = spec.icon;
  const specDoctors = DOCTORS.filter((d) => (DOC_SPECS[spec.slug] || []).includes(d.spec));

  return (
    <>
      {/* Hero */}
      <section className="pt-28 lg:pt-36 pb-16 champagne-gradient">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 text-sm text-[#8A7A5E] mb-6">
            <Link to="/" className="hover:text-[#A6823B]">Acasă</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/#specialitati" className="hover:text-[#A6823B]">Specialități</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-[#4A3F2E] font-medium">{spec.name}</span>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 bg-white/70 text-[#A6823B] text-xs font-semibold px-3.5 py-1.5 rounded-full mb-5">
                <Icon className="w-4 h-4" /> {spec.name}
              </span>
              <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                data-testid="specialty-hero-title"
                className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#2A2521] leading-tight">
                {spec.hero}
              </motion.h1>
              <p className="mt-5 text-lg text-[#4A3F2E] leading-relaxed max-w-xl">{spec.long}</p>
              <a href={CLINIC.phoneRaw} data-testid="specialty-phone-cta"
                className="mt-8 inline-flex items-center gap-3 bg-[#A6823B] hover:bg-[#8A6B2E] text-white font-semibold text-lg px-7 py-4 rounded-full shadow-xl shadow-[#A6823B]/25 transition-all hover:-translate-y-0.5">
                <span className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center pulse-ring">
                  <Phone className="w-4 h-4" />
                </span>
                Sună pentru programare
              </a>
            </div>

            <Reveal delay={0.1}>
              <div className="rounded-3xl overflow-hidden shadow-2xl shadow-[#A6823B]/20 aspect-[4/3]">
                <img src={spec.img} alt={spec.hero} className="w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-10">
          <Reveal className="lg:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#A6823B]">Ce oferim</span>
            <h2 className="font-display text-2xl lg:text-3xl font-bold text-[#2A2521] mt-3">Servicii incluse</h2>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {spec.bullets.map((b) => (
                <div key={b} className="flex items-start gap-3 bg-[#FBF7EF] rounded-xl p-4 border border-[#EADFC6]">
                  <span className="w-8 h-8 rounded-lg bg-[#EFE7D6] flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4 text-[#A6823B]" />
                  </span>
                  <span className="text-[#4A3F2E] font-medium">{b}</span>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Booking card */}
          <Reveal delay={0.1}>
            <div className="bg-[#2A2521] rounded-2xl p-7 text-white sticky top-28">
              <h3 className="font-display text-xl font-bold">Programează-te telefonic</h3>
              <p className="text-white/70 text-sm mt-2">Programarea se face rapid, la un simplu telefon.</p>
              <a href={CLINIC.phoneRaw} className="mt-5 flex items-center justify-center gap-2 bg-[#A6823B] hover:bg-[#8A6B2E] text-white font-semibold px-5 py-3.5 rounded-full transition-colors">
                <Phone className="w-4 h-4" /> {CLINIC.phone}
              </a>
              <div className="mt-6 space-y-3 text-sm text-white/80">
                <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#DCC79A]" /> {CLINIC.address}</div>
                {CLINIC.schedule.map((s) => (
                  <div key={s.day} className="flex items-start gap-2"><Clock className="w-4 h-4 mt-0.5 text-[#DCC79A]" /> {s.day}: {s.hours}</div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Doctors for this specialty */}
      {specDoctors.length > 0 && (
        <section className="py-16 lg:py-20 bg-[#FBF7F0]">
          <div className="max-w-7xl mx-auto px-6">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#A6823B]">Echipa medicală</span>
            <h2 className="font-display text-2xl lg:text-3xl font-bold text-[#2A2521] mt-3 mb-8">Medicii specialității</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {specDoctors.map((d) => (
                <div key={d.name} data-testid="specialty-doctor-card"
                  className="group bg-white rounded-2xl border border-[#EADFC6] overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all">
                  {d.img ? (
                    <div className="aspect-[4/5] overflow-hidden bg-[#EFE7D6]">
                      <img src={d.img} alt={d.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  ) : (
                    <div className="aspect-[4/5] overflow-hidden bg-[#EFE7D6] flex flex-col items-center justify-center text-[#A6823B]">
                      <UserRound className="w-16 h-16 opacity-50" />
                      <span className="text-xs mt-3 text-[#8A7A5E]">Fotografie în curând</span>
                    </div>
                  )}
                  <div className="p-5">
                    <span className="text-xs font-semibold text-[#A6823B] uppercase tracking-wide">{d.spec}</span>
                    <h3 className="font-display font-bold text-[#2A2521] mt-1">{d.name}</h3>
                    <p className="text-sm text-[#8A7A5E]">{d.role}</p>
                    <a href={CLINIC.phoneRaw} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#A6823B] hover:text-[#8A6B2E]">
                      <Phone className="w-4 h-4" /> Programează consult
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery for this specialty */}
      {GAL_CAT[spec.slug] && (
        <Gallery filter={GAL_CAT[spec.slug]} eyebrow="Galerie" title={`Galerie — ${spec.name}`} subtitle="Imagini reale din clinica MEDAS Timișoara." bg="bg-white" />
      )}

      {/* Other specialties */}
      <section className="py-16 bg-[#FBF7EF]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-display text-2xl font-bold text-[#2A2521] mb-8">Alte specialități</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {SPECIALTIES.filter((s) => s.slug !== spec.slug).slice(0, 4).map((s) => (
              <Link key={s.id} to={`/specialitati/${s.slug}`}
                className="group bg-white rounded-xl border border-[#EADFC6] p-5 hover:border-[#A6823B]/40 hover:shadow-lg transition-all">
                <s.icon className="w-6 h-6 text-[#A6823B]" />
                <h3 className="font-display font-bold text-[#2A2521] mt-3">{s.name}</h3>
                <span className="mt-2 inline-flex items-center gap-1 text-sm text-[#A6823B] font-semibold">
                  Vezi detalii <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
